/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * ImpactCalloutSlot — the PDP mission-reminder box ("Every purchase helps save children's
 * lives…" plus a link to the impact details), authored as a Page Designer component.
 *
 * Content model
 * -------------
 * Follows the same shape as `CategoryBannerSlot` (the other migrated content slot in this
 * codebase): structured heading / message / CTA fields, with an optional free-form `markup`
 * escape hatch for merchandising copy that outgrows them. HTML goes through `HtmlFragment`,
 * which renders behind a fixed contentType allowlist and rewrites embedded `<img>` URLs
 * through DIS.
 *
 * Placement
 * ---------
 * Merchants drop it into the PDP page's `impactContent` region (aspect `pdp`, see
 * src/routes/_app.product.$productId.tsx). Rendered with no props — the way the PDP's
 * `<Region errorElement>` fallback renders it — it falls back to the localized default
 * mission copy, so the callout is present before any Page Designer authoring happens.
 */
import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Heart } from 'lucide-react';
import { Link } from '@/components/link';
import HtmlFragment from '@/components/html-fragment';
import { Component } from '@/lib/decorators/component';
import { AttributeDefinition } from '@/lib/decorators/attribute-definition';
import { RegionDefinition } from '@/lib/decorators';
import type { ComponentType } from '@/components/region';
import type { ComponentDesignMetadata } from '@salesforce/storefront-next-runtime/design/react';
import { cn } from '@/lib/utils';
import { useScopedStyleOverride } from '@/hooks/use-scoped-style-override';

/* v8 ignore start - do not test decorators in unit tests, decorator functionality is tested separately*/
@Component('impactCalloutSlot', {
    name: 'PDP Impact Callout',
    description:
        "Mission-reminder box for the product detail page — headline, supporting copy and a link to the impact details. Place inside the Impact Content region on a Product Detail Page. Falls back to the site's default mission copy when left unconfigured.",
    group: 'Content',
})
@RegionDefinition([])
export class ImpactCalloutSlotMetadata {
    @AttributeDefinition({
        id: 'enabled',
        name: 'Enabled',
        description: 'Uncheck to hide the impact callout on this page without removing the component.',
        type: 'boolean',
        defaultValue: true,
    })
    enabled?: boolean;

    @AttributeDefinition()
    heading?: string;

    @AttributeDefinition({
        id: 'message',
        name: 'Message',
        description: 'Supporting copy shown under the heading.',
        type: 'string',
    })
    message?: string;

    @AttributeDefinition()
    linkText?: string;

    @AttributeDefinition({
        id: 'linkUrl',
        name: 'Link URL',
        type: 'url',
        required: false,
    })
    linkUrl?: string;

    @AttributeDefinition({
        id: 'markup',
        name: 'Markup',
        type: 'markup',
        required: false,
        description:
            'Optional free-form HTML replacing the message field above (rendered safely; embedded images are automatically optimized).',
    })
    markup?: string;

    @AttributeDefinition({
        id: 'styleOverride',
        name: 'Style Override',
        description:
            'CSS fragment scoped to this callout instance. Use & as the root selector — it maps to this callout element via CSS nesting and is automatically scoped with a unique attribute at render time.',
        type: 'text',
        required: false,
    })
    styleOverride?: string;
}
/* v8 ignore stop */

export interface ImpactCalloutSlotProps {
    /** Page Designer on/off switch. Only an explicit `false` hides the callout; unset means shown. */
    enabled?: boolean | string;
    heading?: string;
    message?: string;
    linkText?: string;
    linkUrl?: string;
    markup?: string;
    styleOverride?: string;
    className?: string;

    // Page Designer passthrough props — destructured to prevent leaking to the DOM
    regionId?: string;
    component?: ComponentType;
    componentData?: Record<string, Promise<unknown>>;
    designMetadata?: ComponentDesignMetadata;
    data?: unknown;
}

export default function ImpactCalloutSlot({
    enabled,
    heading,
    message,
    linkText,
    linkUrl,
    markup,
    styleOverride,
    className,
    // Exclude PD framework props from the rendered output
    regionId: _regionId,
    component: _component,
    componentData: _componentData,
    designMetadata: _designMetadata,
    data: _data,
}: ImpactCalloutSlotProps): ReactElement | null {
    const { t } = useTranslation('product');
    const { uid, scopedCss } = useScopedStyleOverride(styleOverride, 'data-impact-callout-slot-id');

    // Page Designer can deliver booleans as strings, so treat `'false'` the same as `false`. A disabled
    // component still occupies the `impactContent` region, which keeps the PDP's default-copy fallback
    // from rendering in its place — the callout disappears entirely.
    if (enabled === false || enabled === 'false') {
        return null;
    }

    const trimmedMarkup = markup?.trim();
    const resolvedHeading = heading?.trim() || t('impactCallout.heading');
    const resolvedMessage = message?.trim() || t('impactCallout.message');
    const resolvedLinkText = linkText?.trim() || t('impactCallout.linkText');
    const linkHref = (linkUrl ?? '').trim() || '/about-us';

    return (
        <aside
            data-impact-callout-slot-id={uid}
            className={cn(
                'impact-callout flex items-start gap-4 rounded-lg border border-border-subtle bg-background p-5',
                className
            )}
            aria-label={resolvedHeading}>
            {scopedCss && (
                // eslint-disable-next-line react/no-danger
                <style dangerouslySetInnerHTML={{ __html: scopedCss }} />
            )}
            <Heart className="mt-0.5 size-6 shrink-0 fill-current text-primary" aria-hidden="true" />
            <div className="min-w-0 space-y-1.5">
                <p className="text-base font-stjude-medium leading-6 text-foreground">{resolvedHeading}</p>
                {trimmedMarkup ? (
                    <HtmlFragment content={trimmedMarkup} contentType="plain-text" />
                ) : (
                    <p className="text-sm leading-6 text-muted-foreground">{resolvedMessage}</p>
                )}
                <Link
                    to={linkHref}
                    className="inline-flex items-center gap-1 text-sm font-stjude-medium text-primary hover:underline underline-offset-2">
                    {resolvedLinkText}
                    <span aria-hidden="true">→</span>
                </Link>
            </div>
        </aside>
    );
}
